import { create } from 'zustand';

import { TaskStatus, MessageType, ConnectionState } from '@optidev/shared-types';
import { Message, ActiveTool } from '@/components/agent/AgentMessage';

// Internal config (not exported - only used by evictLRU)
const MAX_CACHED_SESSIONS = 5;

// Re-export for convenience
export type { Message, ActiveTool };
export { MessageType, TaskStatus, ConnectionState };

// Internal types (not exported - only used within store)
interface TaskState {
  taskId: string;
  status: TaskStatus;
  userMessageId?: string; // Optional - may not be provided by server event
  startedAt: number;
  error?: string;
  queuedReason?: string; // Reason task is queued (e.g., 'container_paused')
}

interface SessionState {
  messages: Message[];
  activeTasks: TaskState[];
  lastAccessed: number;
  // NEW: Session-specific error and paused task state
  error: { code: string; message: string } | null;
  pausedTask: { taskId: string; errorCode: string; errorMessage: string } | null;
}

// NEW: Auto-commit notification type
interface AutoCommitNotification {
  sessionId?: string;              // Optional - only provided for agent commits
  status: 'created' | 'failed';
  commitId?: string;
  commitSha?: string;
  filesCount?: number;
  message?: string;
  error?: string;
  timestamp: number;
}

// NEW: Session title updated notification type
interface SessionTitleUpdatedNotification {
  sessionId: string;
  title: string;
  timestamp: number;
}

// NEW: BYOC error notification type (for modal display)
export interface ByocErrorNotification {
  sessionId: string;
  errorType: string; // 'oauth_refresh_failed' | 'api_key_invalid' | etc.
  title?: string;
  message?: string;
  taskId?: string;
  resetTime?: string; // For spending cap errors, e.g., "11pm"
}

// ============================================================
// Store Interface
// ============================================================

interface AgentChatStore {
  // State
  activeSessionId: string | null;
  sessionCache: Map<string, SessionState>;

  // NEW: Global socket state
  connectionState: ConnectionState;

  // NEW: For toast notifications
  latestAutoCommit: AutoCommitNotification | null;

  // NEW: For session title updated trigger
  sessionTitleUpdated: SessionTitleUpdatedNotification | null;

  // NEW: BYOC error for modal display
  byocError: ByocErrorNotification | null;

  // Selectors
  getSession: (sessionId: string) => SessionState | undefined;
  getMessages: (sessionId: string) => Message[];
  hasSessionData: (sessionId: string) => boolean;

  // Session actions
  switchSession: (sessionId: string) => void;

  // Message actions (simplified)
  setMessages: (sessionId: string, messages: Message[]) => void;
  upsertMessage: (sessionId: string, message: Message) => void;
  updateMessage: (sessionId: string, messageId: string, updates: Partial<Message>) => void;

  // Streaming convenience methods
  appendContent: (sessionId: string, messageId: string, content: string) => void;
  addTool: (sessionId: string, messageId: string, tool: ActiveTool) => void;
  updateTool: (sessionId: string, messageId: string, toolUseId: string, updates: Partial<ActiveTool>) => void;

  // Task actions
  setActiveTasks: (sessionId: string, tasks: TaskState[]) => void;
  addActiveTask: (sessionId: string, task: TaskState) => void;
  removeActiveTask: (sessionId: string, taskId: string) => void;
  clearActiveTasks: (sessionId: string) => void;

  // NEW: Connection state actions
  setConnectionState: (state: ConnectionState) => void;

  // NEW: Session-specific state actions
  setError: (sessionId: string, error: { code: string; message: string } | null) => void;
  setPausedTask: (sessionId: string, task: { taskId: string; errorCode: string; errorMessage: string } | null) => void;

  // NEW: Auto-commit notifications
  setAutoCommit: (commit: AutoCommitNotification | null) => void;

  // NEW: Session title updated
  setSessionTitleUpdated: (data: SessionTitleUpdatedNotification | null) => void;

  // NEW: BYOC error for modal display
  setByocError: (error: ByocErrorNotification | null) => void;

  // NEW: Merge with recovery (stale-while-revalidate)
  initSessionWithMerge: (
    sessionId: string,
    apiMessages: Message[],
    activeTasks: { taskId: string; userMessageId: string; status: string }[]
  ) => void;
}

// ============================================================
// Helper Functions
// ============================================================

/**
 * Evict least recently used session from cache
 */
function evictLRU(cache: Map<string, SessionState>, currentId: string): void {
  if (cache.size <= MAX_CACHED_SESSIONS) return;

  let oldestId: string | null = null;
  let oldestTime = Infinity;

  cache.forEach((session, id) => {
    if (id !== currentId && session.lastAccessed < oldestTime) {
      oldestTime = session.lastAccessed;
      oldestId = id;
    }
  });

  if (oldestId) {
    cache.delete(oldestId);
  }
}

// ============================================================
// Duplicate Chunk Prevention
// ============================================================

// Track last appended content per message to prevent duplicate chunks
// (e.g., from socket reconnection event replay)
// Limited to 50 entries to prevent memory growth - old entries auto-evicted
const MAX_CHUNK_TRACKING = 50;
const lastAppendedChunk = new Map<string, string>();

function trackChunk(key: string, content: string): void {
  // Evict oldest if at capacity
  if (lastAppendedChunk.size >= MAX_CHUNK_TRACKING) {
    const firstKey = lastAppendedChunk.keys().next().value;
    if (firstKey) lastAppendedChunk.delete(firstKey);
  }
  lastAppendedChunk.set(key, content);
}

// ============================================================
// Store Implementation
// ============================================================

export const useAgentChatStore = create<AgentChatStore>((set, get) => ({
  // Initial state
  activeSessionId: null,
  sessionCache: new Map(),

  // NEW: Global socket state
  connectionState: ConnectionState.DISCONNECTED,

  // NEW: For toast notifications
  latestAutoCommit: null,

  // NEW: For session title updated trigger
  sessionTitleUpdated: null,

  // NEW: BYOC error for modal display
  byocError: null,

  // =====================
  // Selectors
  // =====================

  getSession: (sessionId: string) => {
    return get().sessionCache.get(sessionId);
  },

  getMessages: (sessionId: string) => {
    return get().sessionCache.get(sessionId)?.messages || [];
  },

  hasSessionData: (sessionId: string) => {
    const session = get().sessionCache.get(sessionId);
    return !!(session && session.messages.length > 0);
  },

  // =====================
  // Session Actions
  // =====================

  switchSession: (sessionId: string) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const session = cache.get(sessionId);

      if (session) {
        cache.set(sessionId, {
          ...session,
          lastAccessed: Date.now(),
        });
      }

      return {
        activeSessionId: sessionId,
        sessionCache: cache,
      };
    });
  },

  // =====================
  // Message Actions
  // =====================

  setMessages: (sessionId: string, messages: Message[]) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const existing = cache.get(sessionId);

      cache.set(sessionId, {
        messages,
        lastAccessed: Date.now(),
        activeTasks: existing?.activeTasks || [],
        error: existing?.error || null,
        pausedTask: existing?.pausedTask || null,
      });

      evictLRU(cache, sessionId);

      return {
        activeSessionId: sessionId,
        sessionCache: cache,
      };
    });
  },

  upsertMessage: (sessionId: string, message: Message) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const session = cache.get(sessionId);

      if (!session) {
        // Create new session with this message
        cache.set(sessionId, {
          messages: [message],
          lastAccessed: Date.now(),
          activeTasks: [],
          error: null,
          pausedTask: null,
        });
        evictLRU(cache, sessionId);
        return { sessionCache: cache, activeSessionId: sessionId };
      }

      // Find existing message by ID first
      let existingIndex = session.messages.findIndex((m) => m.id === message.id);
      let matchedById = existingIndex >= 0;

      // Also check by taskId to prevent duplicates when id changes (e.g., taskId -> entryId)
      if (existingIndex < 0 && message.taskId) {
        existingIndex = session.messages.findIndex((m) => m.taskId === message.taskId);
      }

      let newMessages: Message[];
      if (existingIndex >= 0) {
        const existingMessage = session.messages[existingIndex];
        newMessages = [...session.messages];

        if (matchedById) {
          // Matched by ID - full replacement is safe
          newMessages[existingIndex] = message;
        } else {
          // Matched by taskId - MERGE instead of replace
          // Preserve existing id/entryId if they differ (stream-complete already updated them)
          // This prevents overwriting assistantMessageId back to taskId
          // Also preserve longer content to prevent text loss during race conditions
          const existingContent = existingMessage.content || '';
          const newContent = message.content || '';
          newMessages[existingIndex] = {
            ...existingMessage,
            ...message,
            // Preserve the longer content to prevent text loss during streaming
            content: existingContent.length >= newContent.length ? existingContent : newContent,
            // Preserve the existing id if it's different from message.id
            // (means stream-complete already updated it to assistantMessageId)
            id: existingMessage.id !== message.id ? existingMessage.id : message.id,
            entryId: existingMessage.entryId || message.entryId,
          };
        }
      } else {
        // Append new message
        newMessages = [...session.messages, message];
      }

      cache.set(sessionId, {
        ...session,
        messages: newMessages,
        lastAccessed: Date.now(),
      });

      return { sessionCache: cache };
    });
  },

  updateMessage: (sessionId: string, messageId: string, updates: Partial<Message>) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const session = cache.get(sessionId);

      if (!session) {
        console.warn(`[updateMessage] Session ${sessionId} not found, cannot update message ${messageId}`);
        return state;
      }

      // Find by id first, then fallback to taskId for streaming messages
      // This handles the case where id was updated by initSessionWithMerge or stream-complete
      let messageIndex = session.messages.findIndex((m) => m.id === messageId);
      if (messageIndex < 0) {
        // Fallback: find by taskId property (persists even after id changes)
        messageIndex = session.messages.findIndex((m) => m.taskId === messageId);
      }
      if (messageIndex < 0) {
        console.warn(`[updateMessage] Message ${messageId} not found in session ${sessionId}`);
        return state;
      }

      const newMessages = [...session.messages];
      newMessages[messageIndex] = {
        ...newMessages[messageIndex],
        ...updates,
      };

      cache.set(sessionId, {
        ...session,
        messages: newMessages,
        lastAccessed: Date.now(),
      });

      return { sessionCache: cache };
    });
  },

  // =====================
  // Streaming Convenience Methods
  // =====================

  appendContent: (sessionId: string, messageId: string, content: string) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      let session = cache.get(sessionId);

      // If session doesn't exist, create it with a new message
      if (!session) {
        console.warn(`[appendContent] Session ${sessionId} not found, creating with new message`);
        session = {
          messages: [{
            id: messageId,
            taskId: messageId,
            type: MessageType.ASSISTANT,
            content: content,
            timestamp: new Date(),
            status: TaskStatus.RUNNING,
            isLoading: false,
          }],
          activeTasks: [],
          pausedTask: null,
          error: null,
          lastAccessed: Date.now(),
        };
        cache.set(sessionId, session);
        return { sessionCache: cache };
      }

      // Find by id first, then fallback to taskId for streaming messages
      let messageIndex = session.messages.findIndex((m) => m.id === messageId);
      if (messageIndex < 0) {
        messageIndex = session.messages.findIndex((m) => m.taskId === messageId);
      }

      // If message not found, create it with the content
      if (messageIndex < 0) {
        console.warn(`[appendContent] Message ${messageId} not found in session ${sessionId}, creating new message`);
        const newMessages = [...session.messages, {
          id: messageId,
          taskId: messageId,
          type: MessageType.ASSISTANT,
          content: content,
          timestamp: new Date(),
          status: TaskStatus.RUNNING,
          isLoading: false,
        }];
        cache.set(sessionId, {
          ...session,
          messages: newMessages,
          lastAccessed: Date.now(),
        });
        return { sessionCache: cache };
      }

      // Prevent duplicate consecutive chunks (e.g., from socket reconnection replay)
      const chunkKey = `${sessionId}-${messageId}`;
      if (lastAppendedChunk.get(chunkKey) === content) {
        console.log('[appendContent] Skipping duplicate consecutive chunk');
        return state;
      }
      trackChunk(chunkKey, content);

      const newMessages = [...session.messages];
      newMessages[messageIndex] = {
        ...newMessages[messageIndex],
        content: newMessages[messageIndex].content + content,
      };

      cache.set(sessionId, {
        ...session,
        messages: newMessages,
        lastAccessed: Date.now(),
      });

      return { sessionCache: cache };
    });
  },

  addTool: (sessionId: string, messageId: string, tool: ActiveTool) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const session = cache.get(sessionId);

      if (!session) return state;

      // Find by id first, then fallback to taskId for streaming messages
      let messageIndex = session.messages.findIndex((m) => m.id === messageId);
      if (messageIndex < 0) {
        messageIndex = session.messages.findIndex((m) => m.taskId === messageId);
      }
      if (messageIndex < 0) return state;

      const msg = session.messages[messageIndex];
      const existingTools = msg.activeTools || [];

      // Skip if tool already exists
      if (existingTools.some((t) => t.toolUseId === tool.toolUseId)) {
        return state;
      }

      const newMessages = [...session.messages];
      newMessages[messageIndex] = {
        ...msg,
        activeTools: [...existingTools, tool],
      };

      cache.set(sessionId, {
        ...session,
        messages: newMessages,
        lastAccessed: Date.now(),
      });

      return { sessionCache: cache };
    });
  },

  updateTool: (sessionId: string, messageId: string, toolUseId: string, updates: Partial<ActiveTool>) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const session = cache.get(sessionId);

      if (!session) return state;

      // Find by id first, then fallback to taskId for streaming messages
      let messageIndex = session.messages.findIndex((m) => m.id === messageId);
      if (messageIndex < 0) {
        messageIndex = session.messages.findIndex((m) => m.taskId === messageId);
      }
      if (messageIndex < 0) return state;

      const msg = session.messages[messageIndex];
      if (!msg.activeTools) return state;

      const toolIndex = msg.activeTools.findIndex((t) => t.toolUseId === toolUseId);
      if (toolIndex < 0) return state;

      const newTools = [...msg.activeTools];
      newTools[toolIndex] = { ...newTools[toolIndex], ...updates };

      const newMessages = [...session.messages];
      newMessages[messageIndex] = {
        ...msg,
        activeTools: newTools,
      };

      cache.set(sessionId, {
        ...session,
        messages: newMessages,
        lastAccessed: Date.now(),
      });

      return { sessionCache: cache };
    });
  },

  // =====================
  // Task Actions
  // =====================

  setActiveTasks: (sessionId: string, tasks: TaskState[]) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const session = cache.get(sessionId);

      if (!session) {
        cache.set(sessionId, {
          messages: [],
          lastAccessed: Date.now(),
          activeTasks: tasks,
          error: null,
          pausedTask: null,
        });
      } else {
        cache.set(sessionId, {
          ...session,
          activeTasks: tasks,
          lastAccessed: Date.now(),
        });
      }

      return { sessionCache: cache };
    });
  },

  addActiveTask: (sessionId: string, task: TaskState) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const session = cache.get(sessionId);

      if (!session) {
        cache.set(sessionId, {
          messages: [],
          lastAccessed: Date.now(),
          activeTasks: [task],
          error: null,
          pausedTask: null,
        });
      } else {
        // Avoid duplicates
        const existing = session.activeTasks.find((t) => t.taskId === task.taskId);
        if (existing) return state;

        cache.set(sessionId, {
          ...session,
          activeTasks: [...session.activeTasks, task],
          lastAccessed: Date.now(),
        });
      }

      return { sessionCache: cache };
    });
  },

  removeActiveTask: (sessionId: string, taskId: string) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const session = cache.get(sessionId);

      if (!session) return state;

      cache.set(sessionId, {
        ...session,
        activeTasks: session.activeTasks.filter((t) => t.taskId !== taskId),
        lastAccessed: Date.now(),
      });

      return { sessionCache: cache };
    });
  },

  clearActiveTasks: (sessionId: string) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const session = cache.get(sessionId);

      if (!session) return state;

      cache.set(sessionId, {
        ...session,
        activeTasks: [],
        lastAccessed: Date.now(),
      });

      return { sessionCache: cache };
    });
  },

  // =====================
  // NEW: Connection State Actions
  // =====================

  setConnectionState: (state: ConnectionState) => {
    set({ connectionState: state });
  },

  // =====================
  // NEW: Session-Specific State Actions
  // =====================

  setError: (sessionId: string, error: { code: string; message: string } | null) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const session = cache.get(sessionId);

      if (!session) {
        cache.set(sessionId, {
          messages: [],
          lastAccessed: Date.now(),
          activeTasks: [],
          error,
          pausedTask: null,
        });
      } else {
        cache.set(sessionId, {
          ...session,
          error,
          lastAccessed: Date.now(),
        });
      }

      return { sessionCache: cache };
    });
  },

  setPausedTask: (sessionId: string, task: { taskId: string; errorCode: string; errorMessage: string } | null) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const session = cache.get(sessionId);

      if (!session) {
        cache.set(sessionId, {
          messages: [],
          lastAccessed: Date.now(),
          activeTasks: [],
          error: null,
          pausedTask: task,
        });
      } else {
        cache.set(sessionId, {
          ...session,
          pausedTask: task,
          lastAccessed: Date.now(),
        });
      }

      return { sessionCache: cache };
    });
  },

  // =====================
  // NEW: Auto-Commit Notifications
  // =====================

  setAutoCommit: (commit: AutoCommitNotification | null) => {
    set({ latestAutoCommit: commit });
  },

  // =====================
  // NEW: Session Title Updated
  // =====================

  setSessionTitleUpdated: (data: SessionTitleUpdatedNotification | null) => {
    set({ sessionTitleUpdated: data });
  },

  // =====================
  // NEW: BYOC Error for Modal Display
  // =====================

  setByocError: (error: ByocErrorNotification | null) => {
    set({ byocError: error });
  },

  // =====================
  // NEW: Merge with Recovery (Stale-While-Revalidate)
  // =====================

  initSessionWithMerge: (
    sessionId: string,
    apiMessages: Message[],
    activeTasks: { taskId: string; userMessageId: string; status: string }[]
  ) => {
    set((state) => {
      const cache = new Map(state.sessionCache);
      const cachedSession = cache.get(sessionId);
      const cachedMessages = cachedSession?.messages || [];

      // === STALE-WHILE-REVALIDATE MERGE ===
      // Simple rule: RUNNING tasks → LOCAL first. COMPLETED → API.

      // 1. Build set of running task identifiers
      const runningTasks = activeTasks.filter(
        (t) => t.status === TaskStatus.RUNNING || t.status === TaskStatus.QUEUED
      );
      const runningTaskIds = new Set(runningTasks.map((t) => t.taskId));
      const runningUserMessageIds = new Set(
        runningTasks.filter((t) => t.userMessageId).map((t) => t.userMessageId)
      );

      // 2. Separate API messages: completed vs running
      const completedMessages: Message[] = [];
      const apiRunningMessages = new Map<string, Message>(); // taskId/userMessageId -> message

      apiMessages.forEach((m: Message) => {
        // Check if this message belongs to a running task
        const isRunning =
          (m.userMessageId && runningUserMessageIds.has(m.userMessageId))
          || (m.taskId && runningTaskIds.has(m.taskId));

        if (isRunning) {
          // Store API running message as fallback
          if (m.taskId) apiRunningMessages.set(m.taskId, m);
          if (m.userMessageId) apiRunningMessages.set(m.userMessageId, m);
        } else {
          // COMPLETED: use API message
          completedMessages.push(m);
        }
      });

      // 3. For RUNNING tasks: take LOCAL first, fallback to API
      const runningMessages: Message[] = [];
      runningTasks.forEach((task) => {
        // Find LOCAL message for this task
        const localMsg = cachedMessages.find(
          (m: Message) =>
            (m.taskId === task.taskId ||
              (m.userMessageId && m.userMessageId === task.userMessageId))
        );

        if (localMsg) {
          // USE LOCAL entirely - has streaming state, tools, content
          runningMessages.push({
            ...localMsg,
            taskId: task.taskId,
            status: TaskStatus.RUNNING,
          });
          console.log('[AgentChatStore] RUNNING: using LOCAL for', task.taskId.slice(0, 8));
        } else {
          // Fallback to API message if no local
          const apiMsg = apiRunningMessages.get(task.taskId) ||
                        apiRunningMessages.get(task.userMessageId);
          if (apiMsg) {
            runningMessages.push({
              ...apiMsg,
              taskId: task.taskId,
              status: TaskStatus.RUNNING,
            });
            console.log('[AgentChatStore] RUNNING: using API (no local) for', task.taskId.slice(0, 8));
          }
        }
      });

      // 4. Combine: completed from API + running from LOCAL
      const allMessages = [...completedMessages, ...runningMessages];

      // 5. Deduplicate by ID and taskId only
      // NOTE: userMessageId links assistant messages to parent user message - multiple
      // assistant messages can share the same userMessageId, so it's NOT for deduplication
      const seenIds = new Set<string>();
      const seenTaskIds = new Set<string>();
      const mergedMessages = allMessages.filter((m: Message) => {
        if (seenIds.has(m.id)) return false;
        if (m.taskId && seenTaskIds.has(m.taskId)) return false;
        seenIds.add(m.id);
        if (m.taskId) seenTaskIds.add(m.taskId);
        return true;
      });

      // 6. Sort by timestamp
      mergedMessages.sort((a, b) => {
        const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
        const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
        return timeA - timeB;
      });

      // 7. Convert activeTasks to TaskState format
      const taskStates: TaskState[] = activeTasks.map((t) => ({
        taskId: t.taskId,
        status: t.status as TaskStatus,
        userMessageId: t.userMessageId,
        startedAt: Date.now(),
      }));

      // 8. Update cache
      cache.set(sessionId, {
        messages: mergedMessages,
        activeTasks: taskStates,
        lastAccessed: Date.now(),
        error: cachedSession?.error || null,
        pausedTask: cachedSession?.pausedTask || null,
      });

      evictLRU(cache, sessionId);

      console.log('[AgentChatStore] Merge:', {
        api: apiMessages.length,
        local: cachedMessages.length,
        merged: mergedMessages.length,
        running: runningTasks.length,
      });

      return {
        activeSessionId: sessionId,
        sessionCache: cache,
      };
    });
  },
}));
